import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'reactn';
import { LOCATIONS } from '../../../../../constants';
import { fetchReentryPlans, MAX_REENTRY_PLANS_PAGE_SIZE } from '../../api/reentryPlansApi';
import {
  buildReentryPlanDetailsPath,
  REENTRY_PLANS_DEFAULT_PAGE_SIZE,
} from '../../constants/reentry-planning.constants';
import {
  createViewPlanClickHandler,
  getLocationFilterOptions,
  hasCasePlanAccess,
  mapLocationFilterToLocationIds,
  REENTRY_PLANS_LOCATION_COLUMN,
} from '../../utils/reentry-planning.util';
import { ViewPlanLink } from './ReentryPlansTable.styles';

const noopViewPlanHandler = () => {};

const LEARNER_CELL_FILTER_MAP = {
  username: 'learnerUsername',
  firstName: 'learnerFirstName',
  lastName: 'learnerLastName',
  docId: 'docId',
};

const SORT_COLUMN_MAP = {
  LearnerCell: 'learnerName',
  learnerName: 'learnerName',
  status: 'status',
  updatedAt: 'updatedAt',
};

const ALLOWED_SORT_FIELDS = new Set(['updatedAt', 'learnerName', 'status']);

export const normalizeReentryPlanFilters = (filters) => {
  if (!Array.isArray(filters)) {
    return filters && typeof filters === 'object' ? filters : {};
  }

  return filters.reduce((acc, filter) => {
    if (!filter?.columnName || filter.columnName === REENTRY_PLANS_LOCATION_COLUMN) {
      return acc;
    }

    let value = filter.value;
    if (Array.isArray(value)) {
      value = value.join(',');
    }

    if (value != null && value !== '') {
      const paramName = LEARNER_CELL_FILTER_MAP[filter.preDefinedPropName] || filter.columnName;
      acc[paramName] = value;
    }
    return acc;
  }, {});
};

export const mapSortColumnToApi = (columnName) => {
  const mapped = SORT_COLUMN_MAP[columnName] || columnName;
  return ALLOWED_SORT_FIELDS.has(mapped) ? mapped : 'updatedAt';
};

const renderViewPlanAction = (onViewPlan, plan) => {
  const handleViewPlan = createViewPlanClickHandler(onViewPlan, plan);

  return (
    <ViewPlanLink
      variant="body1"
      role="button"
      tabIndex={0}
      onClick={handleViewPlan}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleViewPlan();
        }
      }}
    >
      View Plan
    </ViewPlanLink>
  );
};

export const mapResponseToGridRows = (
  data = [],
  onViewPlan = noopViewPlanHandler,
  canViewPlan = false,
) =>
  data.map((plan) => ({
    rowData: {
      id: plan.planId,
      learnerFirstName: plan.learnerFirstName || '',
      learnerLastName: plan.learnerLastName || '',
      learnerUsername: plan.learnerUsername || '',
      docId: plan.docId || '',
      learnerPersonId: plan.learnerPersonId,
      planId: plan.planId,
      status: plan.status || '',
      updatedAt: plan.updatedAt || '',
      action: canViewPlan ? () => renderViewPlanAction(onViewPlan, plan) : null,
    },
    preDefineComponentProps: {
      LearnerCell: {
        username: plan.learnerUsername || '',
        firstName: plan.learnerFirstName || '',
        lastName: plan.learnerLastName || '',
        docId: plan.docId || '',
      },
    },
  }));

export const useReentryPlansTableHook = ({ refreshSignal } = {}) => {
  const [global] = useGlobal();
  const navigate = useNavigate();
  const canViewPlan = useMemo(
    () => hasCasePlanAccess(global.permissions),
    [global.permissions],
  );
  const [plans, setPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(REENTRY_PLANS_DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState([]);

  const locationFilterOptions = useMemo(() => getLocationFilterOptions(LOCATIONS), []);

  const handleViewPlan = useCallback(
    (plan) => {
      if (plan?.planId) {
        navigate(buildReentryPlanDetailsPath(plan.planId));
      }
    },
    [navigate],
  );

  const loadPlans = useCallback(async () => {
    const locationIds = mapLocationFilterToLocationIds(filters, LOCATIONS);

    setIsLoading(true);
    try {
      const filterValues = normalizeReentryPlanFilters(filters);
      const response = await fetchReentryPlans({
        ...(locationIds ? { locationIds } : {}),
        page,
        pageSize,
        sortBy,
        sortOrder: sortDirection,
        filters: filterValues,
      });

      const planData = Array.isArray(response?.results) ? response.results : [];
      const count = response?.totalCount ?? planData.length;

      setPlans(mapResponseToGridRows(planData, handleViewPlan, canViewPlan));
      setTotalCount(Number(count));
    } catch {
      setPlans([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [canViewPlan, filters, handleViewPlan, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans, refreshSignal]);

  const onPageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const onPageSizeChange = useCallback((nextPageSize) => {
    setPage(1);
    const clampedSize = Math.min(
      Math.max(1, Number(nextPageSize) || REENTRY_PLANS_DEFAULT_PAGE_SIZE),
      MAX_REENTRY_PLANS_PAGE_SIZE,
    );
    setPageSize(clampedSize);
  }, []);

  const onSortChange = useCallback((columnName, direction) => {
    setPage(1);
    setSortBy(mapSortColumnToApi(columnName));
    setSortDirection(direction);
  }, []);

  const onFilterChange = useCallback((nextFilters) => {
    setPage(1);
    setFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return {
    plans,
    totalCount,
    isLoading,
    pageSize,
    locationFilterOptions,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    reloadPlans: loadPlans,
    canViewPlan,
  };
};
